import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  CustomButton,
  CustomSectionTitle,
  CustomSingleSelect,
  CustomTextInput,
  CustomCalendarInput,
  CustomTwoColInputGroup,
  CustomTruncateText,
  FixedActionBar,
  CustomPdfUploader,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import { Can } from "@/components/auth/Can";
import {
  fetchDocumentManagement,
  type DocumentDefinition,
} from "@/store/documentManagementSlice";
import {
  fallbackCorporateDocs,
  fallbackIndividualDocs,
} from "@/components/PropertyDetail/PrivateArea/privateAreaDocsFallback";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { documentIcon } from "@ippon/shared-assets";
import type { Customer } from "@ippon/shared-types";
import {
  calculateContractMonthlyAmount,
  calculateTotalTsuboUnitPrice,
} from "@ippon/shared-utils";
import OtherFeesInput from "@/components/PropertyDetail/shared/OtherFeesInput";
import CustomerSelectModal from "@/components/PropertyDetail/Unit/sections/CustomerSelectModal";
import { useTsuboLinkedAmounts } from "@/hooks/useTsuboLinkedAmounts";

interface ContractorTabProps {
  /** PreviewDrawer 等に埋め込まれている場合 true (FixedActionBar の追従挙動切り替え) */
  embedded?: boolean;
  view: {
    occupancyStatus: string;
    status: string;
    contractorType: string;
    tenantName: string;
    tenantPhone: string;
    tenantEmail: string;
    companyName: string;
    representativeLastName: string;
    representativeFirstName: string;
    companyPhone: string;
    companyEmail: string;
    startDate: string;
    endDate: string;
    monthlyRent: number | string;
    rentUnitPrice: number | string;
    maintenanceFee: number | string;
    maintenanceFeeUnitPrice: number | string;
    keyMoney: number | string;
    deposit: number | string;
    depositAfter: number | string;
    rentStartDate: string;
    remarks: string;
    lastDepartureDate: string;
  };
  current: any;
  isAncillary?: boolean;
  areaTsubo?: number;
  contractorForm: any;
  rentrollForm: any;
  documentsCount: number;
  contractDocuments: Array<{ url: string; name: string; category: string }>;
  handleContractorInlineSubmit: (data: any) => void;
  handleRentrollInlineSubmit: (data: any) => void;
  setIsNewContractOpen: (v: boolean) => void;
  /** 新規契約 (顧客紐づけなし) を開く */
  onNewContractBlank?: () => void;
  /** 既存顧客を選んで新規契約を開く */
  onNewContractFromCustomer?: (c: Customer) => void;
  setIsEditContractOpen: (v: boolean) => void;
  setIsDepartureProcessOpen: (v: boolean) => void;
  handleDownloadDocument: (doc: { url: string; name: string }) => void;
  otherFees: Array<{ label: string; amount: string }>;
  setOtherFees: React.Dispatch<
    React.SetStateAction<Array<{ label: string; amount: string }>>
  >;
}

// 物件詳細 (PropertyOverview) と同じレイアウトで使う共通寸法
const LABEL_WIDTH = "120px";
const INPUT_WIDTH = "600px";

const ContractorTab: React.FC<ContractorTabProps> = ({
  view,
  current: _current,
  isAncillary = false,
  areaTsubo,
  contractorForm,
  rentrollForm,
  documentsCount,
  contractDocuments,
  handleContractorInlineSubmit,
  handleRentrollInlineSubmit,
  setIsNewContractOpen,
  onNewContractBlank,
  onNewContractFromCustomer,
  setIsEditContractOpen,
  setIsDepartureProcessOpen,
  handleDownloadDocument,
  otherFees,
  setOtherFees,
  embedded = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const docMgmtRecord = useSelector(
    (s: RootState) => s.documentManagement.record,
  );

  // 新規契約ボタン直下のドロップダウン (新規 / 既存顧客から) と顧客選択モーダル
  const [newContractMenuAnchor, setNewContractMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);

  useTsuboLinkedAmounts({
    form: rentrollForm,
    areaTsubo,
    monthlyField: "monthlyRent",
    unitPriceField: "rentUnitPrice",
    enabled: !isAncillary,
  });
  useTsuboLinkedAmounts({
    form: rentrollForm,
    areaTsubo,
    monthlyField: "maintenance_fee",
    unitPriceField: "maintenanceFeeUnitPrice",
    enabled: !isAncillary,
  });
  const totalUnitPrice = calculateTotalTsuboUnitPrice(
    Number(rentrollForm.watch("rentUnitPrice")) || undefined,
    Number(rentrollForm.watch("maintenanceFeeUnitPrice")) || undefined,
  );
  const monthlyRent = rentrollForm.watch("monthlyRent");
  const maintenanceFee = rentrollForm.watch("maintenance_fee");
  const hasMonthlyRent = monthlyRent !== "" && monthlyRent != null;
  const hasMaintenanceFee = maintenanceFee !== "" && maintenanceFee != null;
  const totalRent =
    hasMonthlyRent || hasMaintenanceFee
      ? (Number(monthlyRent) || 0) + (Number(maintenanceFee) || 0)
      : "";
  const pricingType = rentrollForm.watch("pricingType") || "fixed";
  const ancillaryUnitPrice = rentrollForm.watch("unitPrice");
  const ancillaryQuantity = rentrollForm.watch("quantity");
  useEffect(() => {
    if (!isAncillary || pricingType !== "unit_quantity") return;
    const monthlyAmount = calculateContractMonthlyAmount(
      ancillaryUnitPrice === "" ? undefined : Number(ancillaryUnitPrice),
      ancillaryQuantity === "" ? undefined : Number(ancillaryQuantity),
    );
    rentrollForm.setValue(
      "monthlyRent",
      monthlyAmount === undefined ? "" : String(monthlyAmount),
      {
        shouldDirty:
          rentrollForm.getFieldState("unitPrice").isDirty ||
          rentrollForm.getFieldState("quantity").isDirty,
        shouldValidate: true,
      },
    );
  }, [
    ancillaryQuantity,
    ancillaryUnitPrice,
    isAncillary,
    pricingType,
    rentrollForm,
  ]);

  useEffect(() => {
    rentrollForm.setValue("total_rent_per_tsubo", totalUnitPrice ?? "");
  }, [rentrollForm, totalUnitPrice]);
  useEffect(() => {
    rentrollForm.setValue("total_rent", totalRent);
  }, [rentrollForm, totalRent]);

  // 削除予約された URL のリスト (form の documents_to_delete を購読)。
  // X クリックで追加 → 表示から消える。「保存」押下で一括削除 (deletePropertySectionDocument)。
  const pendingDeleteUrls: string[] = (contractorForm.watch?.(
    "documents_to_delete",
  ) || []) as string[];
  const isPendingDelete = (url: string) => pendingDeleteUrls.includes(url);
  const togglePendingDelete = (url: string) => {
    if (pendingDeleteUrls.includes(url)) return;
    contractorForm.setValue(
      "documents_to_delete",
      [...pendingDeleteUrls, url],
      { shouldDirty: true },
    );
  };

  useEffect(() => {
    if (!docMgmtRecord) {
      dispatch(fetchDocumentManagement() as any);
    }
  }, [dispatch, docMgmtRecord]);

  const individualDocDefs: DocumentDefinition[] = useMemo(() => {
    const list =
      (docMgmtRecord?.documents?.individual as
        | DocumentDefinition[]
        | undefined) || fallbackIndividualDocs;
    return list
      .filter((d) => d.is_enabled)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [docMgmtRecord]);

  const corporateDocDefs: DocumentDefinition[] = useMemo(() => {
    const list =
      (docMgmtRecord?.documents?.corporate as
        | DocumentDefinition[]
        | undefined) || fallbackCorporateDocs;
    return list
      .filter((d) => d.is_enabled)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [docMgmtRecord]);

  return (
    <Box
      // AI 画面コンテキスト用 (getPageContext が拾う)。Unit 配下の契約者タブ。
      data-entity="contract"
      data-id={_current?.id || _current?.contract_id || "new"}
      sx={{
        pb: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* 契約者カード */}
      <Box>
        {/* セクションタイトル + ステータス select + 契約更新 / 退去処理 / 新規契約 */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CustomSectionTitle title="テナント情報" addBorder={false} />
            <Chip
              label={view.occupancyStatus === "occupied" ? "入居中" : "空室"}
              size="small"
              sx={{
                backgroundColor:
                  view.occupancyStatus === "occupied" ? "#E8F5E9" : "#F5F5F5",
                color:
                  view.occupancyStatus === "occupied" ? "#2E7D32" : "#616161",
                fontWeight: 600,
                fontSize: "12px",
                "&:hover": {
                  backgroundColor:
                    view.occupancyStatus === "occupied" ? "#E8F5E9" : "#F5F5F5",
                },
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1}>
            {view.occupancyStatus === "occupied" ? (
              <>
                <Can action="contract:edit">
                  <CustomButton
                    label="契約更新"
                    buttonCategory="submit"
                    onClick={() => setIsEditContractOpen(true)}
                  />
                </Can>
                <Can action="contract:edit">
                  <CustomButton
                    label="退去処理"
                    buttonCategory="submit"
                    onClick={() => setIsDepartureProcessOpen(true)}
                  />
                </Can>
              </>
            ) : (
              <>
                <Can action="contract:edit">
                  <Box
                    sx={{ display: "inline-flex" }}
                    onClick={(e) =>
                      setNewContractMenuAnchor(e.currentTarget as HTMLElement)
                    }
                  >
                    <CustomButton
                      label="新規契約"
                      buttonCategory="submit"
                      startIcon={
                        <AddIcon sx={{ fontSize: "18px !important" }} />
                      }
                      endIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
                    />
                  </Box>
                </Can>
                <Menu
                  anchorEl={newContractMenuAnchor}
                  open={Boolean(newContractMenuAnchor)}
                  onClose={() => setNewContractMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{ paper: { sx: { mt: 0.5, minWidth: 220 } } }}
                >
                  <MenuItem
                    sx={{ fontSize: 14 }}
                    onClick={() => {
                      setNewContractMenuAnchor(null);
                      if (onNewContractBlank) onNewContractBlank();
                      else setIsNewContractOpen(true);
                    }}
                  >
                    <PersonAddIcon
                      sx={{ fontSize: 18, mr: 1, color: "#6B7280" }}
                    />
                    新規登録
                  </MenuItem>
                  <MenuItem
                    sx={{ fontSize: 14 }}
                    onClick={() => {
                      setNewContractMenuAnchor(null);
                      setCustomerModalOpen(true);
                    }}
                  >
                    <PersonIcon
                      sx={{ fontSize: 18, mr: 1, color: "#6B7280" }}
                    />
                    既存顧客から登録
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Stack>
        <Divider sx={{ borderColor: "#D9D9D9", mt: 1, mb: 2 }} />

        {view.occupancyStatus === "occupied" ? (
          <Box
            id="contractor-form"
            component="form"
            onSubmit={contractorForm.handleSubmit(handleContractorInlineSubmit)}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* 契約者基本情報 */}
            <Box>
              {view.contractorType === "corporate" ? (
                <>
                  <CustomTextInput
                    label="法人名"
                    name="companyName"
                    register={contractorForm.register}
                    errors={contractorForm.formState.errors}
                    isRequired={false}
                    labelWidth={LABEL_WIDTH}
                    inputWidth={INPUT_WIDTH}
                  />
                  {/* 代表者氏名: 自動付与対象外の複合フィールド。
                       姓名を結合して raw に渡し、AI が一名前として扱えるようにする */}
                  <div
                    data-field="representative_name"
                    data-label="代表者氏名"
                    data-value-raw={`${view.representativeLastName || ""} ${view.representativeFirstName || ""}`.trim()}
                    style={{ display: "contents" }}
                  >
                    <CustomTwoColInputGroup
                      label="代表者氏名"
                      firstName="representativeLastName"
                      lastName="representativeFirstName"
                      placeholderOne="氏"
                      placeholderTwo="名"
                      register={contractorForm.register as any}
                      errors={contractorForm.formState.errors as any}
                      isRequired={false}
                      labelWidth={LABEL_WIDTH}
                      inputWidth={INPUT_WIDTH}
                      inputWidthSp="100%"
                      marginBottom="0px"
                    />
                  </div>
                  <CustomTextInput
                    label="電話番号"
                    name="companyPhone"
                    type="tel"
                    register={contractorForm.register}
                    errors={contractorForm.formState.errors}
                    isRequired={false}
                    labelWidth={LABEL_WIDTH}
                    inputWidth={INPUT_WIDTH}
                  />
                  <CustomTextInput
                    label="メールアドレス"
                    name="companyEmail"
                    type="email"
                    register={contractorForm.register}
                    errors={contractorForm.formState.errors}
                    isRequired={false}
                    labelWidth={LABEL_WIDTH}
                    inputWidth={INPUT_WIDTH}
                  />
                </>
              ) : (
                <>
                  {/* 個人契約者の氏名: 自動付与対象外の複合フィールド。姓名を結合して raw に渡す。 */}
                  <div
                    data-field="tenant_name"
                    data-label="氏名"
                    data-value-raw={view.tenantName || ""}
                    style={{ display: "contents" }}
                  >
                    <CustomTwoColInputGroup
                      label="氏名"
                      firstName="tenantLastName"
                      lastName="tenantFirstName"
                      placeholderOne="氏"
                      placeholderTwo="名"
                      register={contractorForm.register as any}
                      errors={contractorForm.formState.errors as any}
                      isRequired={false}
                      labelWidth={LABEL_WIDTH}
                      inputWidth={INPUT_WIDTH}
                      inputWidthSp="100%"
                      marginBottom="0px"
                    />
                  </div>
                  <CustomTextInput
                    label="電話番号"
                    name="tenantPhone"
                    type="tel"
                    register={contractorForm.register}
                    errors={contractorForm.formState.errors}
                    isRequired={false}
                    labelWidth={LABEL_WIDTH}
                    inputWidth={INPUT_WIDTH}
                  />
                  <CustomTextInput
                    label="メールアドレス"
                    name="tenantEmail"
                    type="email"
                    register={contractorForm.register}
                    errors={contractorForm.formState.errors}
                    isRequired={false}
                    labelWidth={LABEL_WIDTH}
                    inputWidth={INPUT_WIDTH}
                  />
                </>
              )}
              <CustomSingleSelect
                name="contract_form"
                control={contractorForm.control}
                label="契約形態"
                options={[
                  { value: "普通借", label: "普通借" },
                  { value: "定期借", label: "定期借" },
                  { value: "その他", label: "その他" },
                ]}
                labelWidth={LABEL_WIDTH}
                inputWidth={INPUT_WIDTH}
              />
            </Box>

            {/* 契約期間 / 解約予定日 */}
            <Box>
              <Box sx={{ mb: 2 }}>
                <Typography fontSize={12} fontWeight={700} sx={{ mb: 0.5 }}>
                  契約期間
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    width: "100%",
                    maxWidth: INPUT_WIDTH,
                  }}
                >
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <CustomCalendarInput
                      name="startDate"
                      control={contractorForm.control as any}
                      label=""
                      isRequired={false}
                      inputWidth="100%"
                      marginBottom={0}
                    />
                  </Box>
                  <Typography
                    fontSize={14}
                    color="#1f2937"
                    lineHeight={1}
                    sx={{
                      flexShrink: 0,
                      display: "inline-flex",
                      alignItems: "center",
                      alignSelf: "center",
                    }}
                  >
                    〜
                  </Typography>
                  <Box flex={1} sx={{ minWidth: 0 }}>
                    <CustomCalendarInput
                      name="endDate"
                      control={contractorForm.control as any}
                      label=""
                      isRequired={false}
                      inputWidth="100%"
                      marginBottom={0}
                    />
                  </Box>
                </Stack>
              </Box>
              <CustomCalendarInput
                name="cancellation_date"
                control={contractorForm.control as any}
                label="解約予定日"
                isRequired={false}
                labelWidth={LABEL_WIDTH}
                inputWidth={INPUT_WIDTH}
              />
            </Box>

            {/* 備考 */}
            <Box>
              <Typography fontSize={12} fontWeight={700} sx={{ mb: 0 }}>
                備考
              </Typography>
              <CustomTextInput
                label=""
                name="note"
                register={contractorForm.register}
                errors={contractorForm.formState.errors}
                isRequired={false}
                multiline
                rows={3}
                inputWidth={INPUT_WIDTH}
                labelSx={{ display: "none" }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              px: 2,
            }}
          >
            <Box
              component="svg"
              sx={{ width: 80, height: 80, mb: 2, opacity: 0.3 }}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 22V12H15V22"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Box>
            <Typography fontSize={16} fontWeight={600} color="#6b7280" mb={1}>
              契約情報はありません
            </Typography>
            {view.lastDepartureDate && (
              <Typography fontSize={14} color="#9ca3af">
                最終退去日: {view.lastDepartureDate}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* 条件カード（賃料・敷金・賃料起算）。入居中（active 契約）のみ表示・編集可。
          空室時はタイトルごとセクション非表示。 */}
      <Box>
        {view.occupancyStatus === "occupied" && (
          <Box sx={{ mb: 2 }}>
            <CustomSectionTitle title="条件" addBorder />
          </Box>
        )}
        {view.occupancyStatus === "occupied" ? (
          <Box
            id="rentroll-form"
            component="form"
            onSubmit={rentrollForm.handleSubmit(handleRentrollInlineSubmit)}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* 賃料内訳 */}
            <Box>
              {isAncillary && (
                <>
                  <CustomSingleSelect
                    name="pricingType"
                    control={rentrollForm.control}
                    label="料金体系"
                    options={[
                      { value: "fixed", label: "固定月額" },
                      { value: "unit_quantity", label: "単価 × 数量" },
                    ]}
                    inputWidth={INPUT_WIDTH}
                  />
                  {pricingType === "unit_quantity" && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 2,
                        maxWidth: INPUT_WIDTH,
                      }}
                    >
                      <CustomTextInput
                        label="単価（税別）"
                        name="unitPrice"
                        type="number"
                        showYen
                        register={rentrollForm.register}
                        errors={rentrollForm.formState.errors}
                        watch={rentrollForm.watch}
                        setValue={rentrollForm.setValue}
                        inputWidth="100%"
                      />
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "minmax(0, 1fr) 120px",
                          gap: 1,
                        }}
                      >
                        <CustomTextInput
                          label="数量"
                          name="quantity"
                          type="number"
                          register={rentrollForm.register}
                          errors={rentrollForm.formState.errors}
                          inputWidth="100%"
                        />
                        <CustomSingleSelect
                          name="quantityUnit"
                          control={rentrollForm.control}
                          label="単位"
                          options={[
                            { value: "台", label: "台" },
                            { value: "ヶ所", label: "ヶ所" },
                            { value: "基", label: "基" },
                            { value: "一式", label: "一式" },
                          ]}
                          inputWidth="100%"
                        />
                      </Box>
                    </Box>
                  )}
                </>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: isAncillary ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                  maxWidth: INPUT_WIDTH,
                }}
              >
                <CustomTextInput
                  label="月額賃料（税別）"
                  name="monthlyRent"
                  type="number"
                  register={rentrollForm.register}
                  errors={rentrollForm.formState.errors}
                  isRequired={false}
                  inputWidth="100%"
                  showYen
                  watch={rentrollForm.watch}
                  setValue={rentrollForm.setValue}
                  readOnly={isAncillary && pricingType === "unit_quantity"}
                />
                {!isAncillary && (
                  <CustomTextInput
                    label="賃料坪単価"
                    name="rentUnitPrice"
                    type="number"
                    register={rentrollForm.register}
                    errors={rentrollForm.formState.errors}
                    inputWidth="100%"
                    showYen
                    endAdornmentText="円/坪"
                    watch={rentrollForm.watch}
                    setValue={rentrollForm.setValue}
                    disabled={!areaTsubo || areaTsubo <= 0}
                  />
                )}
              </Box>
              {!isAncillary && (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 2,
                      maxWidth: INPUT_WIDTH,
                    }}
                  >
                    <CustomTextInput
                      label="共益費"
                      name="maintenance_fee"
                      type="number"
                      register={rentrollForm.register}
                      errors={rentrollForm.formState.errors}
                      isRequired={false}
                      inputWidth="100%"
                      showYen
                      watch={rentrollForm.watch}
                      setValue={rentrollForm.setValue}
                    />
                    <CustomTextInput
                      label="共益費坪単価"
                      name="maintenanceFeeUnitPrice"
                      type="number"
                      register={rentrollForm.register}
                      errors={rentrollForm.formState.errors}
                      inputWidth="100%"
                      showYen
                      endAdornmentText="円/坪"
                      watch={rentrollForm.watch}
                      setValue={rentrollForm.setValue}
                      disabled={!areaTsubo || areaTsubo <= 0}
                    />
                  </Box>
                </>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: isAncillary ? "1fr" : "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 2,
                  maxWidth: INPUT_WIDTH,
                }}
              >
                <CustomTextInput
                  label="賃料合計"
                  name="total_rent"
                  type="number"
                  register={rentrollForm.register}
                  errors={rentrollForm.formState.errors}
                  isRequired={false}
                  inputWidth="100%"
                  showYen
                  watch={rentrollForm.watch}
                  setValue={rentrollForm.setValue}
                  readOnly
                />
                {!isAncillary && (
                  <CustomTextInput
                    label="賃料合計坪単価"
                    name="total_rent_per_tsubo"
                    type="number"
                    register={rentrollForm.register}
                    errors={rentrollForm.formState.errors}
                    inputWidth="100%"
                    showYen
                    endAdornmentText="円/坪"
                    watch={rentrollForm.watch}
                    setValue={rentrollForm.setValue}
                    readOnly
                  />
                )}
              </Box>
              <CustomTextInput
                label="礼金"
                name="keyMoney"
                type="number"
                register={rentrollForm.register}
                errors={rentrollForm.formState.errors}
                isRequired={false}
                labelWidth={LABEL_WIDTH}
                inputWidth={INPUT_WIDTH}
                showYen
                watch={rentrollForm.watch}
                setValue={rentrollForm.setValue}
              />
              {/* その他費用（任意項目） */}
              <Box sx={{ mb: 2 }}>
                <OtherFeesInput
                  fees={otherFees}
                  onChange={setOtherFees}
                  inputWidth={INPUT_WIDTH}
                />
              </Box>
            </Box>

            {/* 敷金 */}
            <Box>
              <CustomTextInput
                label="敷金（償却前）"
                name="security_deposit_before"
                type="number"
                register={rentrollForm.register}
                errors={rentrollForm.formState.errors}
                isRequired={false}
                labelWidth={LABEL_WIDTH}
                inputWidth={INPUT_WIDTH}
                showYen
                watch={rentrollForm.watch}
                setValue={rentrollForm.setValue}
              />
              <CustomTextInput
                label="敷金（償却後）"
                name="security_deposit_after"
                type="number"
                register={rentrollForm.register}
                errors={rentrollForm.formState.errors}
                isRequired={false}
                labelWidth={LABEL_WIDTH}
                inputWidth={INPUT_WIDTH}
                showYen
                watch={rentrollForm.watch}
                setValue={rentrollForm.setValue}
              />
            </Box>

            {/* 賃料起算 */}
            <Box>
              <CustomCalendarInput
                name="initial_contract_start_date"
                control={rentrollForm.control as any}
                label="当初契約開始日"
                isRequired={false}
                labelWidth={LABEL_WIDTH}
                inputWidth={INPUT_WIDTH}
              />
              <CustomCalendarInput
                name="rent_start_date"
                control={rentrollForm.control as any}
                label="賃料起算日"
                isRequired={false}
                labelWidth={LABEL_WIDTH}
                inputWidth={INPUT_WIDTH}
              />
            </Box>
          </Box>
        ) : null}
      </Box>

      {/* 書類（空室時はタイトルごとセクション非表示） */}
      <Box>
        {view.occupancyStatus === "occupied" && (
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <CustomSectionTitle
                title="書類"
                addBorder={false}
                sx={{
                  mb: 0,
                  width: "fit-content",
                  maxWidth: "calc(100% - 72px)",
                }}
              />
              <Chip
                variant="outlined"
                size="small"
                label={`${documentsCount ?? 0}件`}
                sx={{
                  pointerEvents: "none",
                  height: 20,
                  fontSize: 12,
                  flexShrink: 0,
                }}
              />
            </Stack>
            <Divider sx={{ borderColor: "#D9D9D9" }} />
          </Box>
        )}
        {view.occupancyStatus === "occupied" && (
          <Box sx={{ mb: 2 }}>
            {(view.contractorType === "individual"
              ? individualDocDefs
              : corporateDocDefs
            ).map((def, idx) => {
              // 既存書類のうち、このカテゴリにマッチし、かつ削除予約されていないものを表示
              const existing = contractDocuments.filter(
                (d) =>
                  d.category === def.display_name && !isPendingDelete(d.url),
              );
              return (
                <Box key={def.document_type} sx={{ mt: idx === 0 ? 0 : 2 }}>
                  <CustomPdfUploader
                    name={`documents.${def.document_type}`}
                    label={def.display_name}
                    register={contractorForm.register as any}
                    setValue={contractorForm.setValue as any}
                    errors={contractorForm.formState.errors}
                    width={INPUT_WIDTH}
                    type="multiple"
                    accept=".pdf"
                  />
                  {existing.length > 0 && (
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: INPUT_WIDTH,
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.75,
                      }}
                    >
                      {existing.map((doc, didx) => (
                        <Box
                          key={`${doc.url}-${didx}`}
                          sx={{
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "10px",
                            px: 1.25,
                            py: 0.75,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flex: 1, minWidth: 0 }}
                          >
                            <Box
                              component="img"
                              src={documentIcon}
                              width={16}
                              height={16}
                              alt="doc"
                              sx={{ flexShrink: 0 }}
                            />
                            <Stack spacing={0} sx={{ minWidth: 0, flex: 1 }}>
                              <Box
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  color: "#3e3e3e",
                                }}
                              >
                                <CustomTruncateText text={doc.name} />
                              </Box>
                              <Typography fontSize={12} color="#6b7280">
                                {doc.category}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ flexShrink: 0 }}
                          >
                            <Box
                              component="button"
                              type="button"
                              onClick={() =>
                                handleDownloadDocument({
                                  url: doc.url,
                                  name: doc.name,
                                })
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1.25,
                                py: 0.4,
                                border: "1px solid #0b9dbd",
                                borderRadius: "10px",
                                backgroundColor: "#ffffff",
                                cursor: "pointer",
                                "&:hover": { backgroundColor: "#f0f9fb" },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: '"Noto Sans JP", sans-serif',
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  color: "#0b9dbd",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                ダウンロード
                              </Typography>
                            </Box>
                            <CloseRoundedIcon
                              sx={{
                                color: "#9ca3af",
                                cursor: "pointer",
                                fontSize: 18,
                              }}
                              onClick={() => togglePendingDelete(doc.url)}
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {view.occupancyStatus === "occupied" && (
        <FixedActionBar
          submitLabel="保存"
          isLoading={
            contractorForm.formState.isSubmitting ||
            rentrollForm.formState.isSubmitting
          }
          disabled={
            (!contractorForm.formState.isDirty &&
              !rentrollForm.formState.isDirty) ||
            contractorForm.formState.isSubmitting ||
            rentrollForm.formState.isSubmitting
          }
          embedded={embedded}
          onSubmit={() => {
            if (contractorForm.formState.isDirty) {
              (
                document.getElementById(
                  "contractor-form",
                ) as HTMLFormElement | null
              )?.requestSubmit();
            }
            if (rentrollForm.formState.isDirty) {
              (
                document.getElementById(
                  "rentroll-form",
                ) as HTMLFormElement | null
              )?.requestSubmit();
            }
          }}
        />
      )}
      {/* 既存顧客から登録: 顧客一覧 → 選択で新規契約モーダルへ (顧客を保持) */}
      <CustomerSelectModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        onSelect={(c) => {
          setCustomerModalOpen(false);
          onNewContractFromCustomer?.(c);
        }}
      />
    </Box>
  );
};

export default ContractorTab;
